import JSZip from "jszip";
import { saveAs } from "file-saver";
import { cloneDeep, isEmpty } from "lodash";
import moment from "moment";

import { getCliResults } from "utils/results/api";
import { assemblePackageResults } from "utils/results/runIdentity";
import {
    extractResultValues,
    formatNumDisplay,
    getBarGraphResultsData,
    getUpgradeDhwRowLabels,
    getUpgradeEnvelopeRowLabels,
    getUpgradeHvacRowLabels,
    getUpgradeOtherRowLabels,
} from "utils/upgrades";
import { getCompAreaForCostCalc, getAllSubcomponents } from "utils/enclosure/components";

import { COLOURS_FOR_LEGEND, PARAMETRIC_RESULTS_TABLE, UPGRADE_SELECTION_OPTIONS } from "./constants";

import ParametricUpgradeIcon from "assets/images/icons/ParametricUpgradeIcons.svg?react";
import ParametricPremadeIcon from "assets/images/icons/ParametricPremadeIcon.svg?react";
import { prebuiltEcms } from "features/ParametricAnalysis/templates/preBuiltEcms";
import { collectEcmSubTypesForPackageUpgrade } from "utils/packageUpgradeEcmCategories";

export const ecmCategoryOrder = [
    "ceilingWithAttic",
    "cathedral",
    "wall",
    "expFloor",
    "floorHeader",
    "basementWall",
    "basementSlab",
    "crawlspace",
    "slab",
    "window",
    "skylights",
    "door",
    "airTightness",
    "ventilation",
    "heatingCooling",
    "spaceHeating",
    "secondaryHeating",
    "spaceCooling",
    "primaryHotWater",
    "dwhr",
    "generation",
    "baseLoads",
];

export const ERS_REF_CATEGORIES = [
    "ventilation",
    "heatingCooling",
    "spaceHeating",
    "secondaryHeating",
    "spaceCooling",
    "primaryHotWater",
];

//Helper functions for ERS Reference Document ID generation to match back-end logic

const arrayBufferToBase64Url = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, ""); // strip padding
};

const generateShortToken = async (category, upgradeId) => {
    if (!upgradeId || upgradeId === "None") return "N";
    const data = new TextEncoder().encode(`${category}:${upgradeId}`);
    const hashBuffer = await crypto.subtle.digest("SHA-1", data);
    return arrayBufferToBase64Url(hashBuffer).slice(0, 4);
};

const buildErsRefDocId = async (selections) => {
    const tokens = await Promise.all(
        ERS_REF_CATEGORIES.map((cat) => generateShortToken(cat, selections[cat] || "None"))
    );
    return tokens.join("_");
};

const comboToErsRefDocId = async (combo) => {
    const selections = ERS_REF_CATEGORIES.reduce((acc, cat) => {
        const entry = combo.find((el) => Object.keys(el)[0] === cat);
        acc[cat] = entry ? Object.values(entry)[0] : "None";
        return acc;
    }, {});

    return buildErsRefDocId(selections);
};

const cartesianLexFirstN = (allOpts, maxN) => {
    if (!allOpts || allOpts.length === 0) {
        return [];
    }
    const counts = allOpts.map((dim) => dim.length);
    if (counts.some((c) => c === 0)) {
        return [];
    }

    const cap = maxN == null || !Number.isFinite(maxN) || maxN < 0 ? Number.MAX_SAFE_INTEGER : Math.floor(maxN);

    const indices = counts.map(() => 0);
    const result = [];

    for (let n = 0; n < cap; n++) {
        const combo = indices.map((idx, dim) => allOpts[dim][idx]);
        result.push(combo);

        let d = indices.length - 1;
        while (d >= 0) {
            indices[d]++;
            if (indices[d] < counts[d]) {
                break;
            }
            indices[d] = 0;
            d--;
        }
        if (d < 0) {
            break;
        }
    }

    return result;
};

const getUpgradesToApply = (ecmInputMap = {}, batchType = "parametric") => {
    //Processes the full ecmInputMap (with all info), and returns an object in the form:
    // {
    //     ceilingWithAttic: ["None", "upgrade1", "upgrade2"],
    //     cathedral: ["None"],
    //     wall: ["None"]
    // }

    //input map provided, return only selected options
    //Here we must reinforce the order of ecmInputMap by referring to ecmCategoryOrder
    const userSpecifiedUpgrades = ecmCategoryOrder.reduce((cache, ecmCatId) => {
        if (Object.keys(ecmInputMap).includes(ecmCatId)) {
            const categoryEcms = ecmInputMap[ecmCatId] || {};
            let selectedEcmArray = Object.keys(categoryEcms)
                .filter((key) => categoryEcms[key])
                .filter((key) => {
                    //Don't care if it's passed for validation, but don't check something that's been run
                    if (batchType === "validation") {
                        //In a validation run, we always need the "None" result to be present
                        //Forcing "None" to be present will ensure that new options can get paired with all "None" options, whether or not they've been run before
                        return key === "None" || (categoryEcms[key].active && !categoryEcms[key].firstBatchId);
                    }
                    if (batchType === "ersReference") {
                        return key === "None" || categoryEcms[key].active;
                    }
                    //default case (buildType === "parametric"), check that results passed, previously run combos filtered out later
                    return categoryEcms[key].active && categoryEcms[key].passed;
                })
                .sort(); //sort is needed so we always handle the individual ECMs in the same order (alphabetical)

            return {
                ...cache,
                [ecmCatId]: isEmpty(selectedEcmArray) ? ["None"] : selectedEcmArray, //fallback if empty array is returned
            };
        } else {
            return cache;
        }
    }, {});

    const heatingCoolingUpgrades = (userSpecifiedUpgrades?.heatingCooling || []).filter(
        (upgrade) => upgrade !== "None"
    );

    if (heatingCoolingUpgrades.length > 0) {
        //Some consolidated heatingCooling upgrades are present, we need to remove the separate heating and cooling upgrades
        userSpecifiedUpgrades.spaceHeating = ["None"];
        userSpecifiedUpgrades.spaceCooling = ["None"];
        userSpecifiedUpgrades.secondaryHeating = ["None"];
    }

    return userSpecifiedUpgrades;
};

export const generateSnapErsRefCombos = ({ maxCombos, ecmInputMap = {} }) => {
    const ersRefEcmInputMap = ecmCategoryOrder.reduce((acc, currCat) => {
        if (ERS_REF_CATEGORIES.includes(currCat)) {
            return {
                ...acc,
                [currCat]: ecmInputMap[currCat],
            };
        } else {
            return {
                ...acc,
                [currCat]: { None: ecmInputMap[currCat]?.["None"] ? ecmInputMap[currCat]["None"] : {} },
            };
        }
    }, {});

    const upgradesToApply = getUpgradesToApply(ersRefEcmInputMap, "ersReference");

    const allOpts = Object.keys(upgradesToApply).reduce((cache, type) => {
        const opts = upgradesToApply[type];

        return [...cache, opts.map((val) => ({ [type]: val }))];
    }, []);

    const combos = cartesianLexFirstN(allOpts, maxCombos);

    return combos.slice(0, maxCombos);
};

export const getErsRefReadiness = async (ecmInputMap = {}, docsFromDatabase = {}) => {
    const combos = generateSnapErsRefCombos({ maxCombos: 250, ecmInputMap });
    const requiredDocIds = await Promise.all(
        combos.map(async (combo) => ({ combo, docId: await comboToErsRefDocId(combo) }))
    );

    const getRefRunStatus = (docId) => {
        const doc = docsFromDatabase[docId];
        if (!doc) return "missing";
        if (doc.result === "PASS" && !isEmpty(doc.ersReferenceResults)) return "complete";
        return "failed";
    };

    let completeRefRuns = [];
    let incompleteRefRuns = [];
    const refRuns = requiredDocIds.map(({ docId, combo }) => {
        const status = getRefRunStatus(docId);
        const entry = { docId, combo, status };

        if (status === "complete") {
            completeRefRuns.push(entry);
        } else {
            incompleteRefRuns.push(entry);
        }

        return entry;
    });

    return {
        completeRefRuns,
        incompleteRefRuns,
        ersRefRunsReady: incompleteRefRuns.length === 0,
        refRuns,
    };
};

export const ERS_REF_MECHANICAL_CATEGORIES = [
    "ventilation",
    "heatingCooling",
    "spaceHeating",
    "secondaryHeating",
    "spaceCooling",
    "primaryHotWater",
];

export const getErsRefComboUpgradeLabel = (ecmInputMap = {}, noChangeLabels = {}, category, upgradeId) => {
    if (!upgradeId || upgradeId === "None") {
        return noChangeLabels[category] || "None";
    }

    return ecmInputMap[category]?.[upgradeId]?.label || upgradeId;
};

//Exporting CbatZipFile for legacy analysis
export const saveCbatZipFile = (modelName, data) => {
    let zip = new JSZip();
    const foldername = `${modelName.split(".h2k")[0]}_CBAT_Inputs`;
    let atLeastOneFile = false;
    Object.keys(data).forEach((resKey) => {
        const filename = `${resKey}.csv`;

        if (data[resKey] !== "" && resKey !== "requestNum") {
            atLeastOneFile = true;
            zip.folder(foldername).file(filename, data[resKey]);
        }
    });

    if (atLeastOneFile) {
        zip.generateAsync({ type: "blob" }).then((blob) => {
            saveAs(blob, `${foldername}.zip`);
        });
    }
};

export const checkForResults = async (model, selectedModelId) => {
    const { lastCliRun: { cliId = {} } = {} } = model.modelDetails.modelDetails;
    const cliIds = Object.keys(cliId);

    let baseUpgradesResults = {};

    if (cliIds.length > 0) {
        const runIds = Object.values(cliId).map(({ runId = "" }) => runId);

        const resultsToCheck = [];

        await Promise.all(runIds.map((cliId) => getCliResults({ cliId, modelId: selectedModelId })))
            .then((results) => {
                results.forEach((result) => {
                    if (!isEmpty(result.data)) {
                        resultsToCheck.push({ ...result.data, id: cliId });
                    }
                });

                baseUpgradesResults = assemblePackageResults(results.map(({ data }) => data));
            })
            .catch((error) => {
                return false;
            });

        if (resultsToCheck.length > 0) {
            return { hasResults: true, results: baseUpgradesResults };
        }

        return false;
    }
};

export const getUpgradeSelectionOptions = (upgrades, selectedUpgradeType) => {
    const upgradeOptions = [];

    Object.keys(upgrades).forEach((upgradeKey) => {
        const nestedUpgrades = upgrades[upgradeKey].upgrades;

        if (!nestedUpgrades) return;

        if (upgradeKey !== "base" && Object.keys(nestedUpgrades).length > 0) {
            Object.keys(nestedUpgrades).forEach((nestedUpgradeKey) => {
                const {
                    label = {},
                    upgradeType,
                    fields: { constructionType: { id: constructionId = 0 } = {} } = {},
                } = nestedUpgrades[nestedUpgradeKey];

                if (upgradeType === "ceiling") {
                    if ([2, 3].includes(constructionId) && selectedUpgradeType.type === "cathedral")
                        upgradeOptions.push({
                            value: {
                                upgradeKey: nestedUpgradeKey,
                                upgradeSubType: "cathedral",
                                upgradeType: "ceiling",
                                parentUpgradeKey: upgradeKey,
                            },
                            label: label[Object.keys(label)[0]] || "None",
                            labelIcon: ParametricUpgradeIcon,
                        });

                    if ([0, 1, 4].includes(constructionId) && selectedUpgradeType.type === "ceilingWithAttic")
                        upgradeOptions.push({
                            value: {
                                upgradeKey: nestedUpgradeKey,
                                upgradeSubType: "ceilingWithAttic",
                                upgradeType: "ceiling",
                                parentUpgradeKey: upgradeKey,
                            },
                            label: label[Object.keys(label)[0]] || "None",
                            labelIcon: ParametricUpgradeIcon,
                        });
                }

                if (upgradeType === "basement") {
                    if (selectedUpgradeType.type === "basementWall")
                        upgradeOptions.push({
                            value: {
                                upgradeKey: nestedUpgradeKey,
                                upgradeSubType: "basementWall",
                                upgradeType: "basement",
                                parentUpgradeKey: upgradeKey,
                            },
                            label: label["basementWall"],
                            labelIcon: ParametricUpgradeIcon,
                        });

                    if (selectedUpgradeType.type === "basementSlab")
                        upgradeOptions.push({
                            value: {
                                upgradeKey: nestedUpgradeKey,
                                upgradeSubType: "basementSlab",
                                upgradeType: "basement",
                                parentUpgradeKey: upgradeKey,
                            },
                            label: label["basementSlab"] || "None",
                            labelIcon: ParametricUpgradeIcon,
                        });
                }

                if (upgradeType === "window") {
                    if (selectedUpgradeType.type === "window" && !Object.keys(label).includes("skylights")) {
                        upgradeOptions.push({
                            value: {
                                upgradeKey: nestedUpgradeKey,
                                upgradeSubType: "window",
                                upgradeType: "window",
                                parentUpgradeKey: upgradeKey,
                            },
                            label: label[Object.keys(label)[0]] || "None",
                            labelIcon: ParametricUpgradeIcon,
                        });
                    }

                    if (selectedUpgradeType.type === "skylights" && Object.keys(label).includes("skylights"))
                        upgradeOptions.push({
                            value: { upgradeKey: nestedUpgradeKey, upgradeSubType: "skylights", upgradeType: "window" },
                            label: label[Object.keys(label)[0]] || "None",
                            labelIcon: ParametricUpgradeIcon,
                        });
                }

                if (upgradeType === "heatingCooling") {
                    if (selectedUpgradeType.type === "heatingCooling")
                        upgradeOptions.push({
                            value: {
                                upgradeKey: nestedUpgradeKey,
                                upgradeSubType: "heatingCooling",
                                upgradeType: "heatingCooling",
                                parentUpgradeKey: upgradeKey,
                            },
                            label: label["heatingCooling"] || "None",
                            labelIcon: ParametricUpgradeIcon,
                        });

                    if (selectedUpgradeType.type === "spaceHeating")
                        upgradeOptions.push({
                            value: {
                                upgradeKey: nestedUpgradeKey,
                                upgradeSubType: "spaceHeating",
                                upgradeType: "heatingCooling",
                                parentUpgradeKey: upgradeKey,
                            },
                            label: label["spaceHeating"] || "None",
                            labelIcon: ParametricUpgradeIcon,
                        });

                    if (selectedUpgradeType.type === "secondaryHeating")
                        upgradeOptions.push({
                            value: {
                                upgradeKey: nestedUpgradeKey,
                                upgradeSubType: "secondaryHeating",
                                upgradeType: "heatingCooling",
                                parentUpgradeKey: upgradeKey,
                            },
                            label: label["secondaryHeating"] || "None",
                            labelIcon: ParametricUpgradeIcon,
                        });

                    if (selectedUpgradeType.type === "spaceCooling")
                        upgradeOptions.push({
                            value: {
                                upgradeKey: nestedUpgradeKey,
                                upgradeSubType: "spaceCooling",
                                upgradeType: "heatingCooling",
                                parentUpgradeKey: upgradeKey,
                            },
                            label: label["spaceCooling"] || "None",
                            labelIcon: ParametricUpgradeIcon,
                        });
                }

                if (upgradeType === "domesticHotWater") {
                    if (selectedUpgradeType.type === "primaryHotWater")
                        upgradeOptions.push({
                            value: {
                                upgradeKey: nestedUpgradeKey,
                                upgradeSubType: "primaryHotWater",
                                upgradeType: "domesticHotWater",
                                parentUpgradeKey: upgradeKey,
                            },
                            label: label["primaryHotWater"] || "None",
                            labelIcon: ParametricUpgradeIcon,
                        });

                    if (selectedUpgradeType.type === "dwhr")
                        upgradeOptions.push({
                            value: {
                                upgradeKey: nestedUpgradeKey,
                                upgradeSubType: "dwhr",
                                upgradeType: "domesticHotWater",
                                parentUpgradeKey: upgradeKey,
                            },
                            label: label["dwhr"] || "None",
                            labelIcon: ParametricUpgradeIcon,
                        });
                }

                // bathroomFaucets, showerHeads, clothesWasher, dishwasher, lighting, monitoringSys
                // if (upgradeType === "baseLoads" && selectedUpgradeType.type === "baseLoads") {
                //     Object.keys(label).forEach((labelKey) => {
                //         if (labelKey === "bathroomFaucets")
                //             upgradeOptions.push({
                //                 value: {
                //                     upgradeKey: nestedUpgradeKey,
                //                     upgradeSubType: "bathroomFaucets",
                //                     upgradeType: "baseLoads",
                //                 },
                //                 label: label["bathroomFaucets"],
                //                 labelIcon: ParametricUpgradeIcon,
                //             });

                //         if (labelKey === "showerHeads")
                //             upgradeOptions.push({
                //                 value: {
                //                     upgradeKey: nestedUpgradeKey,
                //                     upgradeSubType: "showerHeads",
                //                     upgradeType: "baseLoads",
                //                 },
                //                 label: label["showerHeads"],
                //                 labelIcon: ParametricUpgradeIcon,
                //             });

                //         if (labelKey === "clothesWasher")
                //             upgradeOptions.push({
                //                 value: {
                //                     upgradeKey: nestedUpgradeKey,
                //                     upgradeSubType: "clothesWasher",
                //                     upgradeType: "baseLoads",
                //                 },
                //                 label: label["clothesWasher"],
                //                 labelIcon: ParametricUpgradeIcon,
                //             });

                //         if (labelKey === "dishwasher")
                //             upgradeOptions.push({
                //                 value: {
                //                     upgradeKey: nestedUpgradeKey,
                //                     upgradeSubType: "dishwasher",
                //                     upgradeType: "baseLoads",
                //                 },
                //                 label: label["dishwasher"],
                //                 labelIcon: ParametricUpgradeIcon,
                //             });

                //         if (labelKey === "lighting")
                //             upgradeOptions.push({
                //                 value: {
                //                     upgradeKey: nestedUpgradeKey,
                //                     upgradeSubType: "lighting",
                //                     upgradeType: "baseLoads",
                //                 },
                //                 label: label["lighting"],
                //                 labelIcon: ParametricUpgradeIcon,
                //             });

                //         if (labelKey === "monitoringSys")
                //             upgradeOptions.push({
                //                 value: {
                //                     upgradeKey: nestedUpgradeKey,
                //                     upgradeSubType: "monitoringSys",
                //                     upgradeType: "baseLoads",
                //                 },
                //                 label: label["monitoringSys"],
                //                 labelIcon: ParametricUpgradeIcon,
                //             });
                //     });
                // }

                if (
                    upgradeType === selectedUpgradeType.type &&
                    selectedUpgradeType.type !== "ceiling" &&
                    selectedUpgradeType.type !== "basement" &&
                    selectedUpgradeType.type !== "window" &&
                    selectedUpgradeType.type !== "skylights" &&
                    selectedUpgradeType.type !== "heatingCooling" &&
                    selectedUpgradeType.type !== "domesticHotWater" &&
                    selectedUpgradeType.type !== "baseLoads"
                ) {
                    upgradeOptions.push({
                        value: {
                            upgradeKey: nestedUpgradeKey,
                            upgradeType: selectedUpgradeType.type,
                            upgradeSubType: selectedUpgradeType.type,
                            parentUpgradeKey: upgradeKey,
                        },
                        label: label[Object.keys(label)[0]] || "None",
                        labelIcon: ParametricUpgradeIcon,
                    });
                }
            });
        }
    });

    return upgradeOptions;
};

export const getPrebuiltEcmUpgradeOptions = (category = "") => {
    const { [category]: ecmsInCategory = {} } = prebuiltEcms;

    const upgradeOptions = Object.keys(ecmsInCategory).map((ecmKey) => {
        const { label = "No label found", packageValues: { upgradeType, upgradeSubType } = {} } =
            ecmsInCategory[ecmKey];

        return {
            value: {
                upgradeKey: category,
                upgradeSubType: upgradeSubType,
                upgradeType: upgradeType,
                parentUpgradeKey: ecmKey,
                isPrebuilt: true,
            },
            label: label,
            labelIcon: ParametricPremadeIcon,
        };
    });

    return upgradeOptions;
};

export const getAllUpgradesPackages = (upgrades) => {
    const allUpgrades = {};

    Object.keys(upgrades).forEach((upgradeKey) => {
        const nestedUpgrades = upgrades[upgradeKey].upgrades;

        if (!nestedUpgrades) return;

        if (upgradeKey !== "base" && Object.keys(nestedUpgrades).length > 0) {
            Object.keys(nestedUpgrades).forEach((nestedUpgradeKey) => {
                allUpgrades[nestedUpgradeKey] = nestedUpgrades[nestedUpgradeKey];
            });
        }
    });

    return allUpgrades;
};

export const getUpgradeToAdd = (
    allUpgrades,
    selectedUpgradePackage,
    ecmInputMap,
    index = 0,
    addingNewfromDrawer = false,
    modelData = {}
) => {
    const {
        upgradeKey,
        upgradeSubType,
        upgradeType,

        parentUpgradeKey = null,
        isPrebuilt = false,
    } = selectedUpgradePackage;

    const {
        components = {},
        airTightness = {},
        ventilation = {},
        domesticHotWater = {},
        heatingCooling = {},
    } = modelData || {};

    let selectedUpgrade = addingNewfromDrawer ? allUpgrades : allUpgrades[upgradeKey];

    if (isPrebuilt) {
        selectedUpgrade = prebuiltEcms[upgradeKey]?.[parentUpgradeKey];
    }

    const lastEcmInputComponent = ecmInputMap[upgradeSubType]
        ? Object.entries(ecmInputMap[upgradeSubType])
              .sort((a, b) => a[1].order || 0 - b[1].order || 0)
              .pop()[1]
        : null;

    let orderOfLastComponent = lastEcmInputComponent ? lastEcmInputComponent.order || 0 : 0;

    let selectedUpgradePackageValues = Object.keys(selectedUpgrade).includes("packageValues")
        ? selectedUpgrade.packageValues
        : selectedUpgrade;

    let {
        label,
        cost: { [upgradeSubType]: initCostObject = {} } = {},
        upgradeProcess,
    } = selectedUpgradePackageValues || {};

    let { total: initCostTotal = 0, items: costItems = {} } = initCostObject || {};

    const nestedLabel = selectedUpgrade?.label?.[upgradeSubType] ? selectedUpgrade?.label?.[upgradeSubType] : "";

    const upgradeLabel = label?.[upgradeSubType] ? label?.[upgradeSubType] : nestedLabel || "Label Error";

    //Update total cost for upgrades with matCostArea items
    const selectedComponents = getSelectedComponents(upgradeSubType, upgradeType, components);

    const totalArea = getCompAreaForCostCalc(selectedComponents, components, upgradeSubType, "matCostArea");

    //totalArea is either the total component area or the total internal surface area of the building in the case of airTightness

    let newCostTotal = 0;
    let newCostItems = {};

    if (upgradeSubType === "airTightness") {
        //need the initial and final ACH value
        const { blowerTest: { airChangeRate50Pa: initialACH = 0 } = {} } = airTightness;

        let deltaACH = 0;
        if (upgradeProcess === "achPercent") {
            let { fields: { blowerTest: { airChangeRate50Pa: achReductionFraction = 0 } = {} } = {} } =
                selectedUpgradePackageValues || {};

            const finalACH = initialACH * (1 - achReductionFraction);

            deltaACH = initialACH - finalACH;
        } else {
            let { fields: { blowerTest: { airChangeRate50Pa: finalACH = 0 } = {} } = {} } =
                selectedUpgradePackageValues || {};

            deltaACH = initialACH - finalACH;
        }

        //cost is provided in $ / (sqft * Δ(ACH50))
        Object.keys(costItems).forEach((itemKey) => {
            const { label, total, pricePerUnit = 0 } = costItems[itemKey];
            if (["overallCost"].includes(label)) {
                const newCost = pricePerUnit * totalArea * deltaACH;
                newCostTotal += parseFloat(newCost.toFixed(0));
                newCostItems[itemKey] = {
                    label,
                    total: parseFloat(newCost.toFixed(0)),
                };
            } else {
                newCostTotal += parseFloat(total.toFixed(0));
                newCostItems[itemKey] = costItems[itemKey];
            }
        });
    } else {
        Object.keys(costItems).forEach((itemKey) => {
            const { label, total, pricePerUnit = 0 } = costItems[itemKey];
            if (["matCostArea", "matCostIntWalls", "matCostExtWalls", "matCostSlab"].includes(label)) {
                const newCost = pricePerUnit * totalArea;
                newCostTotal += parseFloat(newCost.toFixed(2));
                newCostItems[itemKey] = {
                    label,
                    pricePerUnit,
                    total: parseFloat(newCost.toFixed(2)),
                    totalArea,
                };
            } else {
                newCostTotal += parseFloat(total.toFixed(2));
                newCostItems[itemKey] = costItems[itemKey];
            }
        });
    }

    if (newCostTotal !== initCostTotal) {
        selectedUpgradePackageValues.cost[upgradeSubType] = {
            total: newCostTotal,
            items: newCostItems,
        };
    }

    if (upgradeSubType === "basementSlab") {
        return {
            [`upgrade-${moment().format("YYYYMMDDHHmmssSS")}-${index}`]: {
                label: upgradeLabel,
                active: true,
                passed: null,
                individualSavings: null,
                noChange: false,
                order: orderOfLastComponent + 1,
                packageValues: {
                    ...selectedUpgradePackageValues,
                    upgradeType,
                    upgradeSubType,
                    cost: !isEmpty(selectedUpgradePackageValues.cost)
                        ? { [upgradeSubType]: selectedUpgradePackageValues.cost[upgradeSubType] }
                        : { [upgradeSubType]: 0 },
                    fields: {
                        ...selectedUpgradePackageValues.fields,
                    },
                    label: { [upgradeSubType]: upgradeLabel },
                    selectedComponents: ["*"],
                    parentUpgradeKey,
                },
            },
        };
    }

    if (upgradeSubType === "basementWall") {
        return {
            [`upgrade-${moment().format("YYYYMMDDHHmmssSS")}-${index}`]: {
                label: upgradeLabel,
                active: true,
                passed: null,
                individualSavings: null,
                noChange: false,
                order: orderOfLastComponent + 1,
                packageValues: {
                    ...selectedUpgradePackageValues,
                    upgradeType,
                    upgradeSubType,
                    cost: !isEmpty(selectedUpgradePackageValues.cost)
                        ? { [upgradeSubType]: selectedUpgradePackageValues.cost[upgradeSubType] }
                        : { [upgradeSubType]: 0 },
                    fields: {
                        ...selectedUpgradePackageValues.fields,
                        floor: {},
                    },
                    label: { [upgradeSubType]: upgradeLabel },
                    selectedComponents: ["*"],
                    parentUpgradeKey,
                },
            },
        };
    }

    if (upgradeSubType === "dwhr" || upgradeSubType === "primaryHotWater") {
        const { primarySystem = {} } = domesticHotWater || {};
        const isWholeDhwObject = Object.keys(selectedUpgradePackageValues.fields).includes("primarySystem");
        let hotWaterUpgradeFields = {};
        if (isWholeDhwObject) {
            hotWaterUpgradeFields = selectedUpgradePackageValues.fields;
        } else if (!isWholeDhwObject && upgradeSubType === "dwhr") {
            hotWaterUpgradeFields = {
                primarySystem: {
                    ...primarySystem,
                    hasDrainWaterHeatRecovery: true,
                    drainWaterHeatRecovery: {
                        ...selectedUpgradePackageValues.fields,
                    },
                },
                secondarySystem: {},
            };
        }
        return {
            [`upgrade-${moment().format("YYYYMMDDHHmmssSS")}-${index}`]: {
                label: upgradeLabel,
                active: true,
                passed: null,
                individualSavings: null,
                noChange: false,
                order: orderOfLastComponent + 1,
                packageValues: {
                    ...selectedUpgradePackageValues,
                    upgradeType,
                    upgradeSubType,
                    cost: !isEmpty(selectedUpgradePackageValues.cost)
                        ? { [upgradeSubType]: selectedUpgradePackageValues.cost[upgradeSubType] }
                        : { [upgradeSubType]: 0 },
                    fields: {
                        ...hotWaterUpgradeFields,
                        secondarySystem: {},
                    },
                    label: {
                        [upgradeSubType]: upgradeLabel,
                    },
                    selectedComponents: ["*"],
                    parentUpgradeKey,
                },
            },
        };
    }

    if (upgradeSubType === "heatingCooling") {
        let heatingCoolingUpgradeFields = selectedUpgradePackageValues.fields;
        const isHeatingSystemEmpty = isEmpty(heatingCoolingUpgradeFields?.heating?.system || {});
        if (isHeatingSystemEmpty) {
            //if heating system is empty, such as for existing furnace hybrid system, take system from base modelData
            heatingCoolingUpgradeFields.heating.system = heatingCooling.heating.system; //take system from base modelData
        }

        return {
            [`upgrade-${moment().format("YYYYMMDDHHmmssSS")}-${index}`]: {
                label: upgradeLabel,
                active: true,
                passed: null,
                individualSavings: null,
                noChange: false,
                order: orderOfLastComponent + 1,
                packageValues: {
                    ...selectedUpgradePackageValues,
                    upgradeType,
                    upgradeSubType,
                    cost: !isEmpty(selectedUpgradePackageValues.cost)
                        ? { [upgradeSubType]: selectedUpgradePackageValues.cost[upgradeSubType] }
                        : { [upgradeSubType]: 0 },
                    fields: {
                        ...heatingCoolingUpgradeFields, //fields here should include everything in heatingCooling section
                    },
                    label: { [upgradeSubType]: upgradeLabel },
                    selectedComponents: ["*"],
                    parentUpgradeKey,
                },
            },
        };
    }

    if (upgradeSubType === "spaceCooling") {
        return {
            [`upgrade-${moment().format("YYYYMMDDHHmmssSS")}-${index}`]: {
                label: upgradeLabel,
                active: true,
                passed: null,
                individualSavings: null,
                noChange: false,
                order: orderOfLastComponent + 1,
                packageValues: {
                    ...selectedUpgradePackageValues,
                    upgradeType,
                    upgradeSubType,
                    cost: !isEmpty(selectedUpgradePackageValues.cost)
                        ? { [upgradeSubType]: selectedUpgradePackageValues.cost[upgradeSubType] }
                        : { [upgradeSubType]: 0 },
                    fields: {
                        ...selectedUpgradePackageValues.fields,
                        heating: {},
                        radiantHeating: {},
                        additionalOpenings: {},
                        supplSystems: {},
                    },
                    label: { [upgradeSubType]: upgradeLabel },
                    selectedComponents: ["*"],
                    parentUpgradeKey,
                },
            },
        };
    }

    if (upgradeSubType === "spaceHeating") {
        return {
            [`upgrade-${moment().format("YYYYMMDDHHmmssSS")}-${index}`]: {
                label: upgradeLabel,
                active: true,
                passed: null,
                individualSavings: null,
                noChange: false,
                order: orderOfLastComponent + 1,
                packageValues: {
                    ...selectedUpgradePackageValues,
                    upgradeType,
                    upgradeSubType,
                    cost: !isEmpty(selectedUpgradePackageValues.cost)
                        ? { [upgradeSubType]: selectedUpgradePackageValues.cost[upgradeSubType] }
                        : { [upgradeSubType]: 0 },
                    fields: {
                        ...selectedUpgradePackageValues.fields,
                        cooling: {},
                        radiantHeating: {},
                        additionalOpenings: {},
                        supplSystems: {},
                    },
                    label: { [upgradeSubType]: upgradeLabel },
                    selectedComponents: ["*"],
                    parentUpgradeKey,
                },
            },
        };
    }

    if (upgradeSubType === "secondaryHeating") {
        return {
            [`upgrade-${moment().format("YYYYMMDDHHmmssSS")}-${index}`]: {
                label: upgradeLabel,
                active: true,
                passed: null,
                individualSavings: null,
                noChange: false,
                order: orderOfLastComponent + 1,
                packageValues: {
                    ...selectedUpgradePackageValues,
                    upgradeType,
                    upgradeSubType,
                    cost: !isEmpty(selectedUpgradePackageValues.cost)
                        ? { [upgradeSubType]: selectedUpgradePackageValues.cost[upgradeSubType] }
                        : { [upgradeSubType]: 0 },
                    fields: {
                        ...selectedUpgradePackageValues.fields,
                        cooling: {},
                        heating: {},
                    },
                    label: { [upgradeSubType]: upgradeLabel },
                    selectedComponents: ["*"],
                    parentUpgradeKey,
                },
            },
        };
    }

    if (upgradeSubType === "airTightness" && upgradeProcess === "achPercent") {
        return {
            [`upgrade-${moment().format("YYYYMMDDHHmmssSS")}-${index}`]: {
                label: label[Object.keys(label)[0]],
                active: true,
                passed: null,
                individualSavings: null,
                noChange: false,
                noEdit: true,
                order: orderOfLastComponent + 1,
                packageValues: {
                    upgradeType,
                    upgradeSubType,
                    upgradeProcess: "achPercent",
                    ...selectedUpgradePackageValues,
                    selectedComponents: ["*"],
                    parentUpgradeKey,
                },
            },
        };
    }

    if (upgradeSubType === "ventilation") {
        const isWholeVentilationObject = Object.keys(selectedUpgradePackageValues.fields).includes("requirements");
        let ventilationUpgradeFields = {};
        if (isWholeVentilationObject) {
            ventilationUpgradeFields = selectedUpgradePackageValues.fields;
        } else {
            ventilationUpgradeFields = {
                ...ventilation,
                wholeHouseSystems: {
                    hrv: {
                        ...selectedUpgradePackageValues.fields,
                    },
                },
            };
        }

        return {
            [`upgrade-${moment().format("YYYYMMDDHHmmssSS")}-${index}`]: {
                label: label[Object.keys(label)[0]],
                active: true,
                passed: null,
                individualSavings: null,
                noChange: false,
                order: orderOfLastComponent + 1,
                packageValues: {
                    ...selectedUpgradePackageValues,
                    upgradeType,
                    upgradeSubType,
                    cost: !isEmpty(selectedUpgradePackageValues.cost)
                        ? { [upgradeSubType]: selectedUpgradePackageValues.cost[upgradeSubType] }
                        : { [upgradeSubType]: 0 },
                    fields: {
                        ...ventilationUpgradeFields,
                    },
                    label: { [upgradeSubType]: upgradeLabel },
                    selectedComponents: ["*"],
                    parentUpgradeKey,
                },
            },
        };
    }

    return {
        [`upgrade-${moment().format("YYYYMMDDHHmmssSS")}-${index}`]: {
            label: label[Object.keys(label)[0]],
            active: true,
            passed: null,
            individualSavings: null,
            noChange: false,
            order: orderOfLastComponent + 1,
            packageValues: {
                upgradeType,
                upgradeSubType,
                ...selectedUpgradePackageValues,
                selectedComponents: ["*"],
                parentUpgradeKey,
            },
        },
    };
};

export const getUpgradesToAdd = (allUpgradesToAdd, ecmInputMap) => {
    const upgradesToAdd = [];

    Object.entries(allUpgradesToAdd).forEach(([key, value], index) => {
        if (value.upgradeType === "baseLoads") return;

        const ecmSubTypes = collectEcmSubTypesForPackageUpgrade(value);

        if (ecmSubTypes.length >= 1) {
            ecmSubTypes.forEach((upgradeSubType) => {
                const selectedUpgradePackage = {
                    upgradeKey: key,
                    upgradeSubType,
                    upgradeType: value.upgradeType,
                };

                const upgradeToAdd = getUpgradeToAdd(allUpgradesToAdd, selectedUpgradePackage, ecmInputMap, index);

                upgradesToAdd.push({ ...upgradeToAdd });
            });
        }
    });

    return upgradesToAdd;
};

export const needsErsRefRunRetry = ({ hasCodeMetrics, ersRefRunsReady, isErsRefRunComplete }) =>
    hasCodeMetrics && !ersRefRunsReady && isErsRefRunComplete;

export const canRunPrecheck = (ecmInputMap) => {
    if (isEmpty(ecmInputMap)) return false;

    let canRun = false;
    // let allUpgrades = {};

    Object.entries(ecmInputMap).forEach(([key, upgrades]) => {
        if (key === "base" || Object.keys(upgrades).length === 1) return;

        // allUpgrades = { ...allUpgrades, ...upgrades };

        if (canRun) return;

        canRun = Object.entries(upgrades)
            .filter(([upgradeKey, { active }]) => active)
            .some(([upgradeKey, { active, individualSavings, passed, noChange }]) => {
                if (upgradeKey === "None") return false;

                return (
                    (active && individualSavings === null) || passed === null || (!passed && individualSavings === null)
                );
            });
    });

    return canRun;
};

export const canRunAnalysis = (ecmInputMap) => {
    if (isEmpty(ecmInputMap)) return false;

    let canRun = false;
    let allUpgrades = {};

    Object.entries(ecmInputMap).forEach(([key, upgrades]) => {
        if (key === "base" || Object.keys(upgrades).length === 1) return;

        const reformattedUpgrades = Object.entries(upgrades).reduce((acc, [upKey, val]) => {
            const newKey = upKey === "None" ? `${key}-None` : upKey;
            acc[newKey] = val;
            return acc;
        }, {});

        allUpgrades = { ...allUpgrades, ...reformattedUpgrades };
    });

    canRun =
        Object.entries(allUpgrades).filter(([upgradeKey, { active }]) => active).length === 0
            ? false
            : Object.entries(allUpgrades)
                  .filter(([upgradeKey, { active }]) => active)
                  .some(([upgradeKey, { active, individualSavings, passed, firstBatchId }]) => {
                      return active && individualSavings !== null && passed !== null && passed && !firstBatchId;
                  });

    return canRun;
};

export const getNumParametricOptionsCombinations = (ecmInputMap = {}) => {
    if (isEmpty(ecmInputMap)) {
        return {
            totalUniqueOptions: 0,
            totalCombinations: 0,
        };
    }

    // Rules for counting total unique options:
    // Always count all active options that are NOT noChange options
    // Count noChange options IF it is active and there are other active options in its category
    const totalUniqueOptions = Object.keys(ecmInputMap).reduce((sum, cat) => {
        const activeOptionsCount =
            (Object.keys(ecmInputMap[cat]).filter((ecmId) => ecmId !== "None" && ecmInputMap[cat][ecmId]?.active) || [])
                .length || 0;
        if (activeOptionsCount === 0) {
            return sum;
        }
        //Can only ever have one noChange option per category
        const { None: { active: noChangeActive = false } = {} } = ecmInputMap[cat];
        return sum + activeOptionsCount + (noChangeActive ? 1 : 0);
    }, 0);

    // Only "No Change" active across categories — no real upgrades selected yet
    if (totalUniqueOptions === 0) {
        return {
            totalUniqueOptions: 0,
            totalCombinations: 0,
        };
    }

    // the PRODUCT of all counts totalUniqueOptions
    const totalCombinations = Object.keys(ecmInputMap).reduce((product, cat) => {
        //Count the active options, and if for some reason we have zero options in a category coerce the value to 1 so we don't multiply by 0
        const currentCatCount = Math.max(
            (Object.keys(ecmInputMap[cat]).filter((ecmId) => ecmInputMap[cat][ecmId]?.active) || []).length,
            1
        );
        return product * currentCatCount;
    }, 1);

    return {
        totalUniqueOptions,
        totalCombinations,
    };
};

export const getPendingParametricRuns = (ecmInputMap = {}, runHistory = {}) => {
    const { totalCombinations } = getNumParametricOptionsCombinations(ecmInputMap);
    const currentResultsSize = Object.values(runHistory).reduce((sum, { size = 0 }) => sum + size, 0);
    const runsToBeUsed = Math.max(0, totalCombinations - currentResultsSize);

    return {
        totalCombinations,
        currentResultsSize,
        runsToBeUsed,
    };
};

/**
 * How many credits are still needed to unlock enough runs for the current analysis size.
 * Accounts for already-unlocked capacity (availableRuns), which itself reflects prior unlocks
 * net of capacity consumed via runHistory in the UsageBar / run gate.
 */
export const getUnlockRunsRequirement = ({ availableRuns = 0, totalCombinations = 0, runsPerCredit = 2500 } = {}) => {
    const shortfallRuns = Math.max(0, Number(totalCombinations) - Number(availableRuns));
    const creditsNeeded = shortfallRuns === 0 ? 0 : Math.ceil(shortfallRuns / runsPerCredit);

    return {
        shortfallRuns,
        creditsNeeded,
        runsPerCredit,
        hasEnoughUnlocked: shortfallRuns === 0,
        runsFromCredits: (credits = 0) => Math.max(0, Number(credits)) * runsPerCredit,
    };
};

/**
 * Efficiently checks if any upgrade in ecmInputMap has leepCostEstimate set to true
 * @param {Object} ecmInputMap - Map of upgrade categories and their upgrades
 * @returns {boolean} - True if any upgrade has leepCostEstimate === true
 */
export const hasInitCostEstimate = (ecmInputMap = {}) => {
    if (!ecmInputMap || Object.keys(ecmInputMap).length === 0) {
        return false;
    }

    // Iterate through categories
    for (const upgradeCategory in ecmInputMap) {
        const categoryUpgrades = ecmInputMap[upgradeCategory];

        if (!categoryUpgrades || typeof categoryUpgrades !== "object") {
            continue;
        }

        // Iterate through upgrades in this category
        for (const upgradeId in categoryUpgrades) {
            const upgrade = categoryUpgrades[upgradeId];

            // Check if packageValues exists and has leepCostEstimate === true
            if (upgrade?.packageValues?.leepCostEstimate === true) {
                return true; // Early return for efficiency
            }
        }
    }

    return false;
};

export const getFilterOptions = (ecmInputMap, currentFilters) => {
    Object.keys(ecmInputMap).forEach((category) => {
        const ecmKeys = Object.keys(ecmInputMap[category]);

        if (ecmKeys.length === 1) return;

        Object.entries(ecmInputMap[category]).forEach(([key, value]) => {
            if (
                currentFilters[category].upgradesOptions.find(
                    (option) => option.value === key || (key === "None" && option.value === `${category}_None`)
                )
            )
                return;

            currentFilters[category] = {
                ...currentFilters[category],
                hasOptions: true,
            };

            if (key === "None" && value.active && value.firstBatchId) {
                currentFilters[category].upgradesOptions.push({
                    ...value,
                    value: `${category}_None`,
                    id: `${category}_None`,
                });

                currentFilters[category].selectedUpgrades.push(`${category}_None`);

                return;
            }

            if (value.active && value.firstBatchId) {
                currentFilters[category].upgradesOptions.push({ ...value, value: key, id: key });
                currentFilters[category].selectedUpgrades.push(key);
            }
        });

        currentFilters[category].upgradesOptions.sort((a, b) => {
            if (a.value.endsWith("_None") && !b.value.endsWith("_None")) {
                return -1;
            }

            if (!a.value.endsWith("_None") && b.value.endsWith("_None")) {
                return 1;
            }

            return 0;
        });
    });

    return currentFilters;
};

export const getTotalCostOfOwnership = (totalCostOfMeasures = 0, timeHorizon = 0, estAnnualOperatingCost = 0) =>
    Number(parseFloat(totalCostOfMeasures + timeHorizon * estAnnualOperatingCost).toFixed(2));

export const getPointsForGraph = (runResults, selectedLegend, initialRunResultsFilters, axes, timeHorizon) => {
    const { value: selectedLegendCategory } = selectedLegend;

    const { xAxis, yAxis } = axes || {};

    const resultsToReturn = {};
    const upgradesToShowInLegend = {};
    // const coloursInUse = [];

    const allResults = getAllResults(runResults);

    const allSelectedUpgrades = Object.values(initialRunResultsFilters).reduce(
        (acc, { selectedUpgrades }) => [...acc, ...selectedUpgrades],
        []
    );

    const allPossibleUpgrades = Object.values(initialRunResultsFilters).reduce(
        (acc, { upgradesOptions = [] }) => [...acc, ...upgradesOptions.map(({ id }) => id)],
        []
    );

    const excludedNoChangeOptions = allPossibleUpgrades.filter(
        (id) => !allSelectedUpgrades.includes(id) && id.includes("_None")
    );

    //initialRunResultsFilters contains the upgradeIds to display (in selectedUpgrades)
    //Also contains all options in upgradesOptions
    const { upgradesOptions: allLegendOptions = [] } = initialRunResultsFilters[selectedLegendCategory] || {};

    //Start by assigning colours based on the legend options, use allLegendOptions (before filters) so that we are consistent with colour selection.
    const allLegendUpgradeIds = allLegendOptions.map(({ id }) => id).sort(); //apply sort here for consistent ordering

    let colourIndex = 0;
    const colourByCategory = allLegendUpgradeIds.reduce((acc, upgradeId) => {
        const { label: upgradeLabel = "Label Error" } = allLegendOptions.find(({ id }) => id === upgradeId);
        if (upgradeId === `${selectedLegendCategory}_None`) {
            upgradesToShowInLegend["NONE"] = { label: "No Change", colourOnGraph: "#646f81" };
            return {
                ...acc,
                [upgradeId]: "#646f81",
            };
        }

        const colourToUse = COLOURS_FOR_LEGEND[colourIndex] || "#646f81";
        colourIndex++;

        upgradesToShowInLegend[upgradeId] = { label: upgradeLabel, colourOnGraph: colourToUse };
        return {
            ...acc,
            [upgradeId]: colourToUse,
        };
    }, {});

    //Now filter out results based on selections in the filters
    Object.entries(allResults).forEach(([resultKey, resultValue]) => {
        const {
            upgradeIds = "",
            listOfECMs = "",
            upgradesPresent = {},
            totalUpgradeCost = 0,
            totalOperatingCost = 0,
        } = resultValue;

        // 1) Check if the upgrade is allowed by seeing if its upgradeId is present in the results data
        // Split the upgradeIds into an array of component=>id pairs
        const upgradeIdsArray = upgradeIds.split(";");

        // Extract only the IDs from the component=>id pairs
        const upgradeComponentIds = upgradeIdsArray.map((pair) => pair.split("=>")[1]);

        const hasMatchingUpgrades = upgradeComponentIds.every((id) => allSelectedUpgrades.includes(id));

        // 2) Check if we're allowing the "no change" option to pass for a category based on the filter selections.
        // An array of the no change upgradeIds that are allowed based on the filters:
        const allowedNoChangeCategories = allSelectedUpgrades.filter((id) => id.includes("_None"));

        const splitEcms = listOfECMs.split(";");

        let excludeResult = false;
        if (!isEmpty(excludedNoChangeOptions)) {
            //break early if we're dealing with a no change option that should be excluded
            excludeResult = excludedNoChangeOptions.some((id) => {
                const category = id.split("_None")[0];
                const indexOfCategory = ecmCategoryOrder.findIndex((ind) => ind === category);
                return splitEcms[indexOfCategory] === "None";
            });
        }

        const isNoChangeAllowed = allowedNoChangeCategories.every((id) => {
            const category = id.split("_None")[0];
            const indexOfCategory = ecmCategoryOrder.findIndex((ind) => ind === category);
            return splitEcms[indexOfCategory] === "None";
        });

        if (excludeResult) {
            //We're excluding a no change options, do not enter other cases
        } else if (hasMatchingUpgrades) {
            //The results contain an ecm allowed through the filter as determined by the presence of its upgradeId

            //Get the upgradeId present for the legend category
            const matchingLegendId = Object.keys(colourByCategory).find((id) => upgradeComponentIds.includes(id));

            resultsToReturn[resultKey] = {
                ...resultValue,
                resultType: selectedLegendCategory,
                colourOnGraph: colourByCategory[matchingLegendId] || "#646f81", //colorToUse,
                totalCostOfOwnership: getTotalCostOfOwnership(totalUpgradeCost, timeHorizon, totalOperatingCost),
            };
        } else if (isNoChangeAllowed) {
            //The results contain a "No Change" ecm for a categoty allowed by the filter
            resultsToReturn[resultKey] = {
                ...resultValue,
                resultType: "NONE",
                colourOnGraph: "#646f81",
                totalCostOfOwnership: getTotalCostOfOwnership(totalUpgradeCost, timeHorizon, totalOperatingCost),
            };
        }

        //Otherwise, the results are filtered out (not included in resultsToReturn)
    });

    return { resultsToReturn, upgradesToShowInLegend };
};

export const getAllResults = (runResults, timeHorizon) => {
    const resultsToReturn = {};

    Object.entries(runResults).forEach(([_, value]) => {
        const { results } = value;

        if (results) {
            Object.entries(results).forEach(([resultKey, resultValue]) => {
                const { totalUpgradeCost = 0, totalOperatingCost = 0 } = resultValue;

                const totalCostOfOwnership = getTotalCostOfOwnership(totalUpgradeCost, timeHorizon, totalOperatingCost);

                resultsToReturn[resultKey] = { ...resultValue, totalCostOfOwnership };
            });
        }
    });

    return resultsToReturn;
};

export const getMaxX = (organizedData) => {
    const numArray = organizedData.map((d) => d.x);

    const minValue = Math.min(...numArray);
    const maxValue = Math.max(...numArray);

    return maxValue + 0.2 * Math.abs(maxValue - minValue);
};

export const getMaxY = (organizedData) => {
    const numArray = organizedData.map((d) => d.y);

    const minValue = Math.min(...numArray);
    const maxValue = Math.max(...numArray);

    return maxValue + 0.2 * Math.abs(maxValue - minValue);
};

export const getMinX = (organizedData) => {
    const numArray = organizedData.map((d) => d.x);

    const minValue = Math.min(...numArray);
    const maxValue = Math.max(...numArray);

    if (minValue > 0) return 0;

    return minValue - 0.2 * Math.abs(maxValue - minValue);
};

export const getMinY = (organizedData) => {
    const numArray = organizedData.map((d) => d.y);

    const minValue = Math.min(...numArray);
    const maxValue = Math.max(...numArray);

    if (minValue > 0) return 0;

    return minValue - 0.2 * Math.abs(maxValue - minValue);
};

/*
    const tableHeaders = [{label:"House Components", id: "houseComponents"}, {label: "Base Model", id: "baseModel"}
                            , ...pinnedRecipes];
    const tabelComponentsRowsWithCells = [[{label: ""}, {"laebl": ""}], []]
    const tableResultsRowsWithCells = [[{label: ""}, {"laebl": ""}], []]

*/
export const buildTableData = (runResults, pinnedRecipes, modelData, modelResults) => {
    const tableHeaders = [
        { label: "House Components", id: "houseComponents" },
        { label: "Base Model", id: "baseModel" },
    ];
    let tabelComponentsRowsWithCells = [];
    let tableResultsRowsWithCells = [];

    const allResults = getAllResults(runResults);
    const sortedPinnedRecipes = cloneDeep(pinnedRecipes).sort((a, b) => a.order - b.order);

    const {
        components = {},
        codes = {},
        airTightness = {},
        heatingCooling = {},
        ventilation = {},
        domesticHotWater = {},
        generation = {},
        program = {},
        baseLoads = {},
    } = modelData;

    const SORTED_TABLE_COMPONENTS_LIST = UPGRADE_SELECTION_OPTIONS;

    // SETTING UP HEADERS
    if (sortedPinnedRecipes.length > 0) {
        sortedPinnedRecipes.forEach((recipe) => {
            tableHeaders.push(recipe);
        });
    }

    // SETTING UP tabelComponentsRowsWithCells
    // Run through sortedPinnedRecipes once

    // Loop through each component and its value
    SORTED_TABLE_COMPONENTS_LIST.forEach(({ label, value: { type, upgradeType } }, componentIndex) => {
        // Initialize the row if it hasn't been initialized yet
        if (!tabelComponentsRowsWithCells[componentIndex]) {
            tabelComponentsRowsWithCells[componentIndex] = [{ label: label }]; // Ensure the static elements are initialized
        }

        // Loop through table headers
        tableHeaders.forEach((header) => {
            const { id: headerId } = header;

            // Handle houseComponents header
            if (headerId === "houseComponents") {
                tabelComponentsRowsWithCells[componentIndex][0] = { label: label }; // Ensure only one label is added
            }

            // Handle baseModel header
            if (headerId === "baseModel") {
                if (!isEmpty(components)) {
                    let baseModelLabel = "None";

                    baseModelLabel = getUpgradeEnvelopeRowLabels({
                        components,
                        codes,
                        airtightness: airTightness,
                        rowId: type,
                        upgradePackage: {},
                    });

                    if (
                        type === "spaceHeating" ||
                        type === "spaceCooling" ||
                        type === "ventilation" ||
                        type === "heatingCooling"
                    ) {
                        baseModelLabel = getUpgradeHvacRowLabels({
                            heatingCooling,
                            ventilation,
                            rowId: type,
                            upgradePackage: {},
                        });
                    }

                    if (type === "primaryHotWater" || type === "dwhr") {
                        baseModelLabel = getUpgradeDhwRowLabels({
                            domesticHotWater,
                            heatingCooling,
                            rowId: type,
                        });
                    }

                    if (type === "generation") {
                        baseModelLabel = getUpgradeOtherRowLabels({
                            generation,
                            program,
                            baseLoads,
                            rowId: type,
                        });
                    }

                    tabelComponentsRowsWithCells[componentIndex][1] = { label: baseModelLabel?.label || "None" };
                }
            }

            // Handle other headers (except houseComponents and baseModel)
            if (headerId !== "houseComponents" && headerId !== "baseModel") {
                const { upgradesPresent = {} } = allResults[headerId] || {};

                // Check if an upgrade is present for the type
                if (upgradesPresent[type]) {
                    // Ensure the label is only pushed if the recipeId and type don't already exist
                    if (!tabelComponentsRowsWithCells[componentIndex].some((cell) => cell.recipeId === headerId)) {
                        tabelComponentsRowsWithCells[componentIndex].push({
                            label: upgradesPresent[type].label,
                            recipeId: headerId,
                        });
                    }
                } else {
                    // If no upgrade is present, push "-" if the recipeId doesn't already exist
                    if (!tabelComponentsRowsWithCells[componentIndex].some((cell) => cell.recipeId === headerId)) {
                        tabelComponentsRowsWithCells[componentIndex].push({ label: "None", recipeId: headerId });
                    }
                }
            }
        });
    });

    const convertValues = {
        W: 0.29307107,
        MJ: 1000,
    };

    // Loop through each component and its value
    PARAMETRIC_RESULTS_TABLE.forEach(
        ({ label, value, idForBase, convert = false, convertTo, ignore = false, isCost = false }, index) => {
            // Initialize the row if it hasn't been initialized yet
            if (ignore) return;

            if (!tableResultsRowsWithCells[index]) {
                tableResultsRowsWithCells[index] = [{ label: label }];
            }

            // Loop through table headers
            tableHeaders.forEach((header) => {
                const { id: headerId } = header;

                // Handle houseComponents header
                if (headerId === "houseComponents") {
                    tableResultsRowsWithCells[index][0] = { label: label }; // Ensure only one label is added
                }

                // Handle baseModel header
                if (headerId === "baseModel") {
                    let exctractedValue = "None";

                    if (idForBase !== "") {
                        exctractedValue = extractResultValues({
                            allPackageResults: { base: { modelData, resultsData: modelResults } },
                            targetPackageId: "base",
                            rowKey: idForBase,
                            fuelPrices: {},
                            upgradePackage: {},
                            packageUiSpecs: {},
                            isBaseColumn: true,
                        });

                        if (
                            idForBase === "primaryHeating" ||
                            idForBase === "primaryDHW" ||
                            idForBase === "baseConsumption" ||
                            idForBase === "lightsAppliances" ||
                            idForBase === "airConditioner" ||
                            idForBase === "spaceCooling" ||
                            idForBase === "hrvFans"
                        ) {
                            exctractedValue =
                                getBarGraphResultsData({ resultsData: modelResults }, idForBase, "base", {})?.amount ||
                                0;
                        }

                        if (convert) {
                            exctractedValue = parseFloat(
                                parseFloat(
                                    String(exctractedValue).includes(",")
                                        ? String(exctractedValue).replace(",", "")
                                        : exctractedValue
                                ) * convertValues[convertTo]
                            ).toFixed(2);
                        }

                        if (
                            // value === "heatLossReductionVSERSRef" ||
                            // value === "overallEnergyPerfVSERSRef" ||
                            value === "totalUpgradeCost"
                        ) {
                            exctractedValue = "0.00";
                        }

                        // Parametric EUI column is kWh/m²; extractResultValues("energyUseIntensity") returns GJ/m².
                        if (value === "eui_kWh" && exctractedValue !== "None" && exctractedValue !== "-") {
                            const euiGjPerM2 = parseFloat(String(exctractedValue).replace(/,/g, ""));
                            if (!isNaN(euiGjPerM2)) {
                                exctractedValue = (euiGjPerM2 * (1 / 0.0036)).toFixed(2);
                            }
                        } else if (
                            exctractedValue !== "None" &&
                            exctractedValue !== "-" &&
                            exctractedValue != null &&
                            !String(exctractedValue).includes("%") &&
                            !String(exctractedValue).includes("$") &&
                            !String(exctractedValue).includes("(")
                        ) {
                            const numericValue = parseFloat(String(exctractedValue).replace(/,/g, ""));
                            if (!isNaN(numericValue)) {
                                exctractedValue = numericValue.toFixed(2);
                            }
                        }
                    }

                    tableResultsRowsWithCells[index][1] = { label: exctractedValue };
                }

                if (headerId !== "baseModel" && headerId !== "houseComponents") {
                    const result = allResults[headerId];

                    const currentRow = tableResultsRowsWithCells[index];

                    if (currentRow.some((cell) => cell.recipeId === headerId)) return;

                    tableResultsRowsWithCells[index].push({
                        label: !isNaN(parseFloat(result[value]))
                            ? isCost
                                ? `$${formatNumDisplay(result[value], 0)}`
                                : parseFloat(result[value]).toFixed(2)
                            : result[value],
                        recipeId: headerId,
                    });
                }
            });
        }
    );

    return { tableHeaders, tabelComponentsRowsWithCells, tableResultsRowsWithCells };
};

export const isAnalysisRunCompelted = (runHistory) => {
    let isCompleted = true;

    if (isEmpty(runHistory)) return true;

    Object.entries(runHistory).forEach(([key, value]) => {
        if (isCompleted) {
            if (!value.complete) isCompleted = false;
        }
    });

    return isCompleted;
};

export const checkIfRunOlderThanTimeout = (runHistory) => {
    const oneHourAgo = moment().subtract(1, "hours");

    let isOlderThanOneHour = false;

    if (isEmpty(runHistory)) return isOlderThanOneHour;

    Object.entries(runHistory).forEach(([key, value]) => {
        if (!isOlderThanOneHour && !value.complete) {
            const { seconds = 0, nanoseconds = 0 } = value.time || {};

            const runTime = moment.unix(seconds + nanoseconds / 1e9);

            if (runTime.isBefore(oneHourAgo)) isOlderThanOneHour = true;
        }
    });

    return isOlderThanOneHour;
};

const getAllWallsWindowsDoors = (components, upgradeH2kType, upgradeType) => {
    let allWallsWindowsDoors = {};

    if (!components) return allWallsWindowsDoors;

    Object.entries(components).forEach(([componentKey, componentValue]) => {
        if (upgradeType === "wall" && componentKey === "wall") {
            allWallsWindowsDoors = { ...allWallsWindowsDoors, ...componentValue };
        }

        Object.entries(componentValue).forEach(([mainSubcomponentKey, value]) => {
            if (!value.subcomponents) return;

            Object.entries(value.subcomponents).forEach(([subcomponentKey, subcomponentValue]) => {
                if (subcomponentKey === "window" && componentKey === "ceiling" && upgradeType === "skylights")
                    allWallsWindowsDoors = { ...allWallsWindowsDoors, ...subcomponentValue };

                if (
                    upgradeH2kType === "window" &&
                    componentKey !== "ceiling" &&
                    upgradeType === "window" &&
                    (subcomponentKey === "window" || subcomponentKey === "door")
                ) {
                    if (subcomponentKey === "window") {
                        allWallsWindowsDoors = { ...allWallsWindowsDoors, ...subcomponentValue };
                    }

                    if (subcomponentKey === "door") {
                        Object.entries(subcomponentValue).forEach(([_, doorValue]) => {
                            Object.entries(doorValue.subcomponents).forEach(([nestedDoorKey, nestedDoorValue]) => {
                                if (nestedDoorKey === "window")
                                    allWallsWindowsDoors = { ...allWallsWindowsDoors, ...nestedDoorValue };
                            });
                        });
                    }
                }

                if (upgradeH2kType === "door" && subcomponentKey === "door")
                    allWallsWindowsDoors = { ...allWallsWindowsDoors, ...subcomponentValue };

                if (upgradeH2kType === "floorHeader" && subcomponentKey === "floorHeader")
                    // allWallsWindowsDoors = { ...allWallsWindowsDoors, ...subcomponentValue };
                    Object.entries(subcomponentValue).forEach(([subSubcomponentKey, subcomponentValue]) => {
                        allWallsWindowsDoors = {
                            ...allWallsWindowsDoors,
                            [subSubcomponentKey]: {
                                ...subcomponentValue,
                                pathTo: `${componentKey}.${mainSubcomponentKey}.subcomponents.${subcomponentKey}.${subSubcomponentKey}`,
                            },
                        };
                    });

                if (upgradeH2kType === "expFloor" && subcomponentKey === "expFloor")
                    // allWallsWindowsDoors = { ...allWallsWindowsDoors, ...subcomponentValue };
                    Object.entries(subcomponentValue).forEach(([subSubcomponentKey, subcomponentValue]) => {
                        allWallsWindowsDoors = {
                            ...allWallsWindowsDoors,
                            [subSubcomponentKey]: {
                                ...subcomponentValue,
                                pathTo: `${componentKey}.${mainSubcomponentKey}.subcomponents.${subcomponentKey}.${subSubcomponentKey}`,
                            },
                        };
                    });
            });
        });
    });

    return allWallsWindowsDoors;
};

export const getSelectedComponents = (upgradeType, upgradeH2kType, components) => {
    const selectedComponents = [];

    if (["window", "door", "floorHeader"].includes(upgradeH2kType) || upgradeType === "window") {
        return getAllSubcomponents(upgradeH2kType, components).map(({ value }) => value) || [];
    }

    const componentsType = components[upgradeH2kType] || {};

    if (!componentsType) return selectedComponents;

    Object.entries(componentsType).forEach(([componentKey, componentValue]) => {
        const { constructionType: { id: constructionId = 0 } = {} } = componentValue || {};

        if (upgradeH2kType === "ceiling") {
            if ([2, 3].includes(constructionId) && upgradeType === "cathedral")
                selectedComponents.push(`${upgradeH2kType}.${componentKey}`);

            if ([0, 1, 4].includes(constructionId) && upgradeType === "ceilingWithAttic")
                selectedComponents.push(`${upgradeH2kType}.${componentKey}`);
        }

        if (upgradeH2kType === "basement" || upgradeH2kType === "crawlspace")
            selectedComponents.push(`${upgradeH2kType}.${componentKey}`);

        if (upgradeH2kType === "window") selectedComponents.push(`${upgradeH2kType}.${componentKey}`);

        if (upgradeH2kType === "door") selectedComponents.push(`${upgradeH2kType}.${componentKey}`);

        if (upgradeH2kType === "wall") selectedComponents.push(`${upgradeH2kType}.${componentKey}`);

        if (upgradeH2kType === "slab") selectedComponents.push(`${upgradeH2kType}.${componentKey}`);

        if (upgradeH2kType === "expFloor") selectedComponents.push(`${upgradeH2kType}.${componentKey}`);

        if (upgradeH2kType === "floorHeader") selectedComponents.push(componentValue.pathTo);
    });

    return selectedComponents;
};

/** Copy an ecmInputMap for a new model, clearing validation fields that must be re-run. */
export const resetEcmInputMapValidation = (ecmInputMap = {}) => {
    const out = cloneDeep(ecmInputMap);

    Object.keys(out).forEach((sectionType) => {
        const upgrades = out[sectionType];
        if (!upgrades || typeof upgrades !== "object") return;

        Object.keys(upgrades).forEach((upgradeId) => {
            const entry = upgrades[upgradeId];
            if (!entry || typeof entry !== "object") return;

            const { firstBatchId, ...rest } = entry;

            if (entry?.noChange) {
                out[sectionType][upgradeId] = rest;
                return;
            }

            out[sectionType][upgradeId] = {
                ...rest,
                passed: null,
                individualSavings: null,
            };
        });
    });

    return out;
};
